import { useEffect, useState } from 'react';
import { Button, Flex, Title } from '@mantine/core';
import { IconPlus } from '@tabler/icons-react';
import UnderlineTabs from 'src/UI/page-nav/UnderlineTabs';
import { Logger } from 'src/utils/Logger';
import CommunitySkillsTab from './CommunitySkillsTab';
import CuratedSkillsTab from './CuratedSkillsTab';
import FirmSkillsTab from './FirmSkillsTab';
import useSkillLibrary from './hooks/useSkillLibrary';
import SkillPreviewModal from './SkillPreviewModal';
import SkillEditorModal from '../manage-siesta-skills/SkillEditorModal';
import useTenantSkills from '../manage-siesta-skills/hooks/useTenantSkills';
import useTenantSkill from '../manage-siesta-skills/hooks/useTenantSkill';

enum SkillLibraryTab {
  Firm = 'firm',
  Curated = 'curated',
  Community = 'community',
}

type EditorState = { mode: 'closed' } | { mode: 'create' } | { mode: 'edit'; skillId: string };

export default function SkillLibraryPage() {
  const [activeTab, setActiveTab] = useState<SkillLibraryTab>(SkillLibraryTab.Firm);
  const [editorState, setEditorState] = useState<EditorState>({ mode: 'closed' });
  const [previewEntryId, setPreviewEntryId] = useState<string | null>(null);

  const {
    data,
    error,
    isLoading: isLibraryLoading,
    isError: isLibraryError,
    refetch,
  } = useSkillLibrary();
  // Same query key as the tab's own read, so this costs no extra request.
  const { data: skills, isLoading, isError } = useTenantSkills();

  const editingSkillId = editorState.mode === 'edit' ? editorState.skillId : null;
  const { data: editingSkill } = useTenantSkill(editingSkillId);

  // Logged rather than surfaced: the firm's own skills do not depend on this request, so a
  // failure costs the tab its name and nothing else.
  useEffect(() => {
    if (error) Logger.error('Failed to load the skill library', error);
  }, [error]);

  const firmName = data?.firmName ?? null;
  const isEmpty = !isLoading && !isError && skills?.length === 0;
  const onFirmTab = activeTab === SkillLibraryTab.Firm;
  const previewEntry =
    [...(data?.curated.items ?? []), ...(data?.community.items ?? [])].find(
      e => e.id === previewEntryId,
    ) ?? null;

  return (
    <Flex direction="column" gap="lg">
      <SkillEditorModal
        opened={
          editorState.mode === 'create' || (editorState.mode === 'edit' && editingSkill != null)
        }
        skill={editorState.mode === 'edit' ? (editingSkill ?? null) : null}
        onClose={() => setEditorState({ mode: 'closed' })}
      />

      <UnderlineTabs
        mode="local"
        value={activeTab}
        onChange={value => setActiveTab(value as SkillLibraryTab)}
        leadingContent={
          <Title order={6} fw={600}>
            Skill library
          </Title>
        }
        trailingContent={
          onFirmTab ? (
            <Button
              variant={isEmpty ? 'default' : 'filled'}
              leftSection={<IconPlus size={16} />}
              onClick={() => setEditorState({ mode: 'create' })}
            >
              Create skill
            </Button>
          ) : undefined
        }
        tabs={[
          { label: firmName ? `${firmName}'s skills` : 'Your skills', value: SkillLibraryTab.Firm },
          { label: 'Curated skills', value: SkillLibraryTab.Curated },
          { label: 'Community skills', value: SkillLibraryTab.Community },
        ]}
      />
      {onFirmTab && (
        <FirmSkillsTab
          firmName={firmName}
          onBrowseCurated={() => setActiveTab(SkillLibraryTab.Curated)}
          onCreate={() => setEditorState({ mode: 'create' })}
          onEdit={skillId => setEditorState({ mode: 'edit', skillId })}
        />
      )}
      {activeTab === SkillLibraryTab.Curated && (
        <CuratedSkillsTab
          page={data?.curated}
          isLoading={isLibraryLoading}
          isError={isLibraryError}
          onRetry={() => refetch()}
          onOpen={setPreviewEntryId}
          firmName={firmName}
        />
      )}
      {activeTab === SkillLibraryTab.Community && (
        <CommunitySkillsTab
          page={data?.community}
          isLoading={isLibraryLoading}
          isError={isLibraryError}
          onRetry={() => refetch()}
          onOpen={setPreviewEntryId}
        />
      )}
      <SkillPreviewModal
        entry={previewEntry}
        firmName={firmName}
        onClose={() => setPreviewEntryId(null)}
      />
    </Flex>
  );
}
